import React, { useEffect, useMemo } from "react";
import Checkbox2 from "../../checkbox/Checkbox2";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_ACTIVE_SERIES_PART_INDEX,
  SET_LAST_INPUT_CHANGE_RECORD,
  SET_PREVIEWS_ACTIVE_SERIES_DETAIL,
  SET_SERIES_STORE,
  SET_SERIES_PART_CHECKED,
  SelecrSeriesStore,
  SelectActiveSeriesIndex,
  SelectActiveSeriesPartIndex,
  SelectSeriesPart,
  SelectIsMulti,
  SET_ACTIVE_SERIES_INDEX,
  SET_IS_MULTI,
} from "../../../../redux/2d-slice/seriesSlice2d";

function SideTableSeriesCheckbox() {
  const dispatch = useDispatch();
  const seriesStore = useSelector(SelecrSeriesStore);
  const activeSeriesIndex = useSelector(SelectActiveSeriesIndex);
  const activeSeriesPartIndex = useSelector(SelectActiveSeriesPartIndex);
  const selectSeriesPart = useSelector(SelectSeriesPart);
  const isMulti = useSelector(SelectIsMulti);

  // Compute a safe active index (if current active is invalid)
  const safeActiveIndex = useMemo(() => {
    if (Number.isInteger(activeSeriesIndex) && seriesStore[activeSeriesIndex]?.AllSeriesPartName?.length) {
      return activeSeriesIndex;
    }
    const found = seriesStore.findIndex((s) => s?.AllSeriesPartName?.length);
    return found === -1 ? null : found;
  }, [activeSeriesIndex, seriesStore]);

  // ensure global activeSeriesIndex points to an existing series (if possible)
  useEffect(() => {
    if (safeActiveIndex === null) {
      // no series available with parts - set to 0 (safe default)
      dispatch(SET_ACTIVE_SERIES_INDEX(0));
    } else if (safeActiveIndex !== activeSeriesIndex) {
      dispatch(SET_ACTIVE_SERIES_INDEX(safeActiveIndex));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveIndex]);

  // // Reset/clamp active part index when the active series changes
  // useEffect(() => {
  //   const parts = seriesStore[safeActiveIndex]?.AllSeriesPartName;
  //   if (parts?.length) {
  //     const clamped = Math.min(
  //       Number.isInteger(activeSeriesPartIndex) ? activeSeriesPartIndex : 0,
  //       parts.length - 1
  //     );
  //     dispatch(SET_ACTIVE_SERIES_PART_INDEX(clamped));
  //   } else {
  //     dispatch(SET_ACTIVE_SERIES_PART_INDEX(0));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [safeActiveIndex, seriesStore]);

  // PageUp / PageDown handling (guarded)
  useEffect(() => {
    const parts = seriesStore[safeActiveIndex]?.AllSeriesPartName;
    if (!parts?.length) return;

    const handleKeyDown = (e) => {
      if (e.key === "PageDown") {
        e.preventDefault();
        const nextIndex = (activeSeriesPartIndex + 1) % parts.length;
        activatePart(nextIndex, false);
      } else if (e.key === "PageUp") {
        e.preventDefault();
        const prevIndex = (activeSeriesPartIndex - 1 + parts.length) % parts.length;
        activatePart(prevIndex, false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveIndex, activeSeriesPartIndex, seriesStore]);

  const activatePart = (index, reset = false) => {
    const parts = seriesStore[safeActiveIndex]?.AllSeriesPartName;
    // console.log(parts)
    if (!parts?.length || index == null || index < 0 || index >= parts.length) return;

    const updatedSeriesStore = seriesStore.map((series, seriesIndex) => {
      if (seriesIndex !== safeActiveIndex) return series;
      return {
        ...series,
        AllSeriesPartName: series.AllSeriesPartName.map((part, partIndex) => ({
          ...part,
          active: reset ? part.active : false,
        })),
      };
    });

    dispatch(SET_SERIES_STORE(updatedSeriesStore));

    if (!reset) {
      dispatch(SET_IS_MULTI(false));
      dispatch(SET_SERIES_PART_CHECKED(new Array(selectSeriesPart.length).fill(false)));

      const resetSeriesStore = updatedSeriesStore.map((series) => ({
        ...series,
        AllSeriesPartName: series.AllSeriesPartName.map((part) => ({
          ...part,
          active: false,
        })),
      }));

      dispatch(SET_SERIES_STORE(resetSeriesStore));
    }

    const isActive = updatedSeriesStore[safeActiveIndex].AllSeriesPartName[index]?.active ?? false;

    dispatch(
      SET_PREVIEWS_ACTIVE_SERIES_DETAIL({
        lastSeriesIndex: safeActiveIndex,
        lastSeriesPartIndex: index,
        isActiveSeriesName: isActive,
      })
    );
    dispatch(SET_ACTIVE_SERIES_PART_INDEX(index));
    dispatch(
      SET_LAST_INPUT_CHANGE_RECORD({
        activeSeriesIndex: null,
        activeSeriesPartIndex: null,
        input: null,
      })
    );
  };

  const handleOnChange = (e, index) => {
    e.stopPropagation();
    const parts = seriesStore[safeActiveIndex]?.AllSeriesPartName;
    if (!parts?.length) return;
    const checked = !!e?.target?.checked;

    const updatedSeriesStore = seriesStore.map((s, sIdx) => {
      if (sIdx === safeActiveIndex) {
        return {
          ...s,
          AllSeriesPartName: s.AllSeriesPartName.map((p, i) =>
            i === index ? { ...p, active: checked } : p
          ),
        };
      }

      if (isMulti && Array.isArray(selectSeriesPart) && selectSeriesPart[sIdx]) {
        return {
          ...s,
          AllSeriesPartName: s.AllSeriesPartName.map((p, i) =>
            i === index ? { ...p, active: checked } : p
          ),
        };
      }

      return s;
    });

    // 🔴 new uncheck logic → clear values in seriesAllfield
    if (!checked) {
      try {
        const targetFields =
          seriesStore[safeActiveIndex]?.seriesAllfield?.[index] ?? [];
        targetFields.forEach((field) => {
          field.resetValue?.();
          if (field.setValue) field.setValue(0);
        });

        if (isMulti) {
          // also clear in other selected series
          selectSeriesPart.forEach((isSelected, sIdx) => {
            if (!isSelected || sIdx === safeActiveIndex) return;
            const otherFields = seriesStore[sIdx]?.seriesAllfield?.[index] ?? [];
            otherFields.forEach((field) => {
              field.resetValue?.();
              if (field.setValue) field.setValue(0);
            });
          });
        }
      } catch (err) {
        console.warn("Uncheck reset issue:", err);
      }
    }

    dispatch(SET_SERIES_STORE(updatedSeriesStore));

    const isActive =
      updatedSeriesStore[safeActiveIndex].AllSeriesPartName[index]?.active ?? false;

    dispatch(
      SET_PREVIEWS_ACTIVE_SERIES_DETAIL({
        lastSeriesIndex: safeActiveIndex,
        lastSeriesPartIndex: index,
        isActiveSeriesName: isActive,
      })
    );
    dispatch(SET_ACTIVE_SERIES_PART_INDEX(index));
  };

  // If no valid series available, render nothing
  if (safeActiveIndex == null) return null;

  const parts = seriesStore[safeActiveIndex]?.AllSeriesPartName ?? [];

  return (
    <>
      {parts.map((partName, index) => {
        const bgColor = index % 2 === 0 ? '#efd3d2'  :  '#dfa7a6';
        return (
          <tr
            key={index + 1}
            style={{
              backgroundColor:
                index === activeSeriesPartIndex ? "#000000ff" : "" || (index % 2 === 0 ? '#efd3d2'  :  '#dfa7a6'),
              cursor: "pointer",
            }}
            className={`nav-item game_name_box game_result${index + 1}`}
            onClick={(e) => {
              if (e.target.type === "checkbox") return;
              e.stopPropagation();
              activatePart(index, false);
            }}
          >
            <td>
              <div className="flex items-center">
                <Checkbox2
                  id={index}
                  checked={partName.active}
                  onChange={(e) => handleOnChange(e, index)}
                />
              </div>
            </td>
            <td style={{ color: index === activeSeriesPartIndex ? "#ffffff" : "#000000" }}>{partName.name}</td>
          </tr>
        );
      })}
    </>
  );

}

export default SideTableSeriesCheckbox;